import { gql } from '@apollo/client';

const GET_DEFAULT_JOB_WAGES = gql`
  query getDefaultJobWages($zipcode: String!, $serviceType: ServiceType!) {
    getJobWages(zipcode: $zipcode, serviceType: $serviceType) {
      legalMinimum {
        amount
        __typename
      }
      defaultMaxWage {
        amount
        __typename
      }
      defaultMinWage {
        amount
        __typename
      }
      maxAllowed {
        amount
        __typename
      }
      averages {
        average {
          amount
          __typename
        }
      }
      __typename
    }
  }
`;

const PROVIDER_NAME_UPDATE = gql`
  mutation providerNameUpdate($input: ProviderNameUpdateInput!) {
    providerNameUpdate(input: $input) {
      __typename

      ... on ProviderNameUpdateSuccess {
        dummy
      }
    }
  }
`;

const CAREGIVER_ATTRIBUTES_UPDATE = gql`
  mutation caregiverAttributesUpdate($input: CaregiverAttributesUpdateInput!) {
    caregiverAttributesUpdate(input: $input) {
      __typename

      ... on CaregiverAttributesUpdateSuccess {
        dummy
      }
    }
  }
`;

const PROVIDER_JOB_INTEREST_UPDATE = gql`
  mutation providerJobInterestUpdate($input: ProviderJobInterestUpdateInput!) {
    providerJobInterestUpdate(input: $input) {
      __typename
      ... on ProviderJobInterestUpdateSuccess {
        dummy
        __typename
      }
    }
  }
`;

const PROVIDER_AVAILABILITY_UPDATE = gql`
  mutation providerAvailabilityUpdate($input: ProviderAvailabilityUpdateInput!) {
    providerAvailabilityUpdate(input: $input) {
      __typename
      ... on ProviderAvailabilityUpdateSuccess {
        dummy
        __typename
      }
    }
  }
`;

const GET_JOB_WAGES = gql`
  query getJobWages($zipcode: String!, $serviceType: ServiceType!) {
    getJobWages(zipcode: $zipcode, serviceType: $serviceType) {
      averages {
        average {
          amount
        }
        minimum {
          amount
        }
        maximum {
          amount
        }
      }
      legalMinimum {
        amount
      }
    }
  }
`;

const GET_AVERAGE_JOB_WAGE = gql`
  query getAverageJobWage($zipcode: String!, $serviceType: ServiceType!) {
    getJobWages(zipcode: $zipcode, serviceType: $serviceType) {
      averages {
        average {
          amount
        }
      }
    }
  }
`;

const GET_NUMBER_OF_JOBS_NEARBY = gql`
  query getNumberOfNewJobsNearby(
    $zipcode: String!
    $serviceType: ServiceType!
    $radius: DistanceInput
  ) {
    getNumberOfNewJobsNearby(zipcode: $zipcode, serviceType: $serviceType, radius: $radius)
  }
`;

const GET_NUMBER_OF_SENIOR_CARE_FACILITIES_NEARBY = gql`
  query getNumberOfSeniorCareFacilitiesNearby(
    $zipcode: String!
    $radius: Int
    $notSure: Boolean!
    $independentLivingFacilities: Boolean!
    $assistedLivingFacilities: Boolean!
    $memoryCareFacilities: Boolean!
  ) {
    getNumberOfSeniorCareFacilitiesNearby(zipcode: $zipcode, radius: $radius) {
      __typename

      ... on GetNumberOfSeniorCareFacilitiesNearbySuccess {
        count @include(if: $notSure)
        countIndependentLivingFacilities @include(if: $independentLivingFacilities)
        countAssistedLivingFacilities @include(if: $assistedLivingFacilities)
        countMemoryCareFacilities @include(if: $memoryCareFacilities)
      }
    }
  }
`;

const PROVIDER_CREATE = gql`
  mutation providerCreate($input: ProviderCreateInput!) {
    providerCreate(input: $input) {
      __typename

      ... on ProviderCreateSuccess {
        authToken
        memberId
        oneTimeToken
        __typename
        memberUuid
      }

      ... on ProviderCreateError {
        errors {
          __typename
          message
        }
        __typename
      }
    }
  }
`;

const SENIOR_CARE_PROVIDER_ATTRIBUTES_UPDATE = gql`
  mutation seniorCareProviderAttributesUpdate($input: SeniorCareProviderAttributesUpdateInput!) {
    seniorCareProviderAttributesUpdate(input: $input) {
      __typename
      ... on SeniorCareProviderAttributesUpdateSuccess {
        dummy
      }
    }
  }
`;

const SENIOR_CARE_SEEKER_CREATE = gql`
  mutation seniorCareSeekerCreate($input: SeniorCareSeekerCreateInput!) {
    seniorCareSeekerCreate(input: $input) {
      ... on SeniorCareSeekerCreateSuccess {
        authToken
        memberId
        oneTimeToken
        memberUuid
        subscriptionDetails {
          businessModel
        }
      }

      ... on SeniorCareSeekerCreateError {
        errors {
          message
        }
      }
    }
  }
`;

const FAVORITE_PROVIDER = gql`
  mutation favoriteProvider($input: FavoriteProviderInput!) {
    favoriteProvider(input: $input) {
      __typename

      ... on FavoriteCaregiverPayload {
        favorite {
          favoriteStatus
          id
        }
      }
      ... on FavoriteProviderInvalidServiceProfile {
        message
      }
    }
  }
`;

const GET_ZIP_CODE_SUMMARY = gql`
  query getZipcodeSummary($zipcode: String) {
    getZipcodeSummary(zipcode: $zipcode) {
      ... on ZipcodeSummary {
        city
        state
        zipcode
        latitude
        longitude
      }

      ... on InvalidZipcodeError {
        message
      }
    }
  }
`;

const GET_INSTANT_BOOK_LOCATION_ELIGIBLE = gql`
  query getInstantBookLocationEligible($zipcode: String!) {
    getInstantBookLocationEligible(zipcode: $zipcode) {
      ... on GetInstantBookLocationEligibleSuccess {
        eligible
      }
    }
  }
`;

const GET_CAREGIVER_COUNT_FOR_JOB = gql`
  query getCaregiverCountForJob(
    $zipcode: String!
    $serviceType: ServiceType!
    $childCareSearchCriteria: ChildCareSearchCriteria
  ) {
    getCaregiverCountForJob(
      zipcode: $zipcode
      serviceType: $serviceType
      childCareSearchCriteria: $childCareSearchCriteria
    ) {
      count
    }
  }
`;

const VALIDATE_MEMBER_EMAIL = gql`
  query validateMemberEmail($email: String!) {
    validateMemberEmail(email: $email) {
      errors {
        __typename
        message
      }
    }
  }
`;

const VALIDATE_MEMBER_PASSWORD = gql`
  query validateMemberPassword($password: String!) {
    validateMemberPassword(password: $password) {
      errors {
        __typename
        message
      }
    }
  }
`;

const SEEKER_CREATE = gql`
  mutation seekerCreate($input: SeekerCreateInput!) {
    seekerCreate(input: $input) {
      __typename

      ... on SeekerCreateSuccess {
        authToken
        memberId
        externalId
        oneTimeToken
        memberUuid
        subscriptionDetails {
          businessModel
        }
      }

      ... on SeekerCreateError {
        errors {
          __typename
          message
        }
      }
    }
  }
`;

const GET_CAREGIVER_ENROLLMENT_STATUS = gql`
  query getCaregiverProfileCompleteness($serviceType: ServiceType!) {
    getCaregiverProfileCompleteness(serviceType: $serviceType) {
      ... on CaregiverProfileCompletenessStatus {
        additionalInfo
        availability
        bio
        education
        jobInterest
        languages
        overallStatus
        qualities
        photo
        services
        subscriptionPlanViewed
        freeGated {
          appDownload
          hourlyRate
          welcomeBack
        }
        firstName
        lastName
        subscriptionPurchased
        multiVerticalEnrollment
        hourlyRatesUpdated
      }
    }
  }
`;

const CHANGE_MEMBER_PASSWORD = gql`
  mutation changeMemberPassword($passwordInput: MemberChangePasswordInput!) {
    memberChangePassword(input: $passwordInput) {
      __typename
      ... on MemberChangePasswordSuccess {
        authToken
      }
      ... on MemberChangePasswordError {
        errors {
          __typename
          message
        }
      }
    }
  }
`;

const CHILD_CARE_PROVIDER_EXPANDED = gql`
  query ListChildCareProvidersExpanded(
    $source: String!
    $zipcode: String!
    $careStartDate: LocalDate
    $childrenDoB: [LocalDate!]
    $distanceWillingToTravel: DistanceInput
    $pageSize: Int
  ) {
    listChildCareProvidersExpanded(
      source: $source
      zipcode: $zipcode
      careStartDate: $careStartDate
      childrenDoB: $childrenDoB
      distanceWillingToTravel: $distanceWillingToTravel
      pageSize: $pageSize
    ) {
      __typename
      ... on ProvidersExpandedSearchSuccess {
        trackingId
        providerSearchResults {
          distanceFromSearchCenter {
            unit
            value
          }
          searchMatchReason
          provider {
            name
            contactDetails {
              leadPhone {
                number
              }
            }
            businessProfileUri
            id
            description
            centerType
            logo {
              urlOriginal
            }
            images {
              urlOriginal
            }
            address {
              addressLine1
              addressLine2
              city
              latitude
              longitude
              preciseLatitude
              preciseLongitude
              state
              zip
            }
            reviews {
              text
              date
              rating
              reviewer {
                firstName
                lastName
              }
            }
            license {
              name
              verifiedDate
              certified
              externalUrl
              administrativeArea
              status
            }
            tourRequestStatus
          }
        }
      }
      ... on ProviderSearchError {
        __typename
        message
      }
    }
  }
`;

const POST_CHILD_CARE_LEAD = gql`
  mutation postChildCareLead($childCareLeadInput: ChildCareLeadCreateInput!) {
    childCareLeadCreate(input: $childCareLeadInput) {
      __typename
      batchId
      leadProviderAssociations {
        leadId
        providerId
      }
    }
  }
`;

const POST_AUTO_ACCEPT_CHILD_CARE_LEAD = gql`
  mutation autoAcceptChildCareLeadPublish(
    $autoAcceptChildCareLeadPublishInput: AutoAcceptChildCareLeadPublishInput!
  ) {
    autoAcceptChildCareLeadPublish(input: $autoAcceptChildCareLeadPublishInput) {
      batchId
    }
  }
`;

export const GET_MEMBER_QUERY = gql`
  query Member {
    member {
      displayName
      email
      firstName
      lastName
      id
      legacyId
      isPremium
    }
  }
`;

// it's not ready yet
const GET_TAX_CALCULATOR_PRICING = gql`
  query calculateChildCareTaxCreditSaving($input: ChildCareTaxCreditSavingCalculationInput!) {
    calculateChildCareTaxCreditSaving(input: $input) {
      __typename
      ... on ChildCareTaxCreditSavingCalculationSuccess {
        options {
          originalTotal {
            amount
            currencyCode
          }
          discountedTotal {
            amount
            currencyCode
          }
          savingPercentage
          savingAmount {
            amount
            currencyCode
          }
          subServiceType
        }
      }
      ... on ChildCareTaxCreditSavingCalculationError {
        message
      }
    }
  }
`;

const SENIOR_CARE_FACILITY_LEAD_GENERATE = gql`
  mutation seniorCareFacilityLeadGenerate($input: SeniorCareFacilityLeadGenerateInput!) {
    seniorCareFacilityLeadGenerate(input: $input) {
      __typename
      ... on SeniorCareFacilityLeadGenerateSuccess {
        dummy
      }
    }
  }
`;

const HIRED_CAREGIVER_CRM_EVENT_CREATE = gql`
  mutation CzenCrmEventHiredCaregiverCreate($input: CzenCrmEventHiredCaregiverInput!) {
    czenCrmEventHiredCaregiverCreate(input: $input) {
      ... on CzenCrmEventResponse {
        success
      }
    }
  }
`;

const DAYCARE_CRM_EVENT_CREATE = gql`
  mutation czenCrmEventCreateLeadsSubmitted($input: CzenCrmEventLeadsSubmittedInput!) {
    czenCrmEventCreateLeadsSubmitted(input: $input) {
      success
    }
  }
`;

const SENIOR_CARE_ASSISTED_LIVING_PROVIDERS = gql`
  query seniorCareAssistedLivingProviders(
    $zipcode: String!
    $source: String!
    $maxNumberOfResults: Int
    $facilityType: SeniorCommunityType
  ) {
    seniorCareAssistedLivingProviders(
      zipcode: $zipcode
      source: $source
      maxNumberOfResults: $maxNumberOfResults
      facilityType: $facilityType
    ) {
      __typename
      ... on ProviderSearchSuccess {
        __typename
        trackingId
        providerSearchResults {
          distanceFromSearchCenter {
            unit
            value
          }
          provider {
            name
            description
            id
            centerType
            images {
              urlSmall
              urlMedium
            }
            address {
              addressLine1
              city
              state
              latitude
              longitude
              zip
            }
            license {
              certified
            }
            seniorAssistedLivingCenter {
              attributes {
                careServices {
                  alsCare
                  callSystemTwentyFourHours
                  dailyLivingAssistance
                  diabetesCare
                  healthCareProviderCoordination
                  hospiceCare
                  insulinInjections
                  mealPreparationAndService
                  medicationManagement
                  memoryCare
                  mentalWellnessCare
                  mildCognitiveImpairmentCare
                  nursingTwelveToSixteenHours
                  nursingTwentyFourHours
                  parkinsonsCare
                  physicalTherapy
                  preventativeHealthScreenings
                  rehabilitationProgram
                  transportationArrangement
                  transportationArrangementMedical
                  respiteCare
                  strokeCare
                  supervisionTwentyFourHours
                }
                communityTypes {
                  assistedLiving
                  continuingCare
                  independentLiving
                  memoryCare
                }
                facilityAmenities {
                  barberOrBeautySalon
                  cafeOrBistro
                  computerCenter
                  diningRoom
                  fitnessRoom
                  gameRoom
                  garden
                  library
                  marketOnSite
                  outdoorPatio
                  outdoorSpace
                  privateDiningRooms
                  religiousOrMeditationCenter
                  swimmingPool
                  spaOrJacuzzi
                  wellnessCenter
                }
                foodAmenities {
                  allMealsProvided
                  anytimeDining
                  diabetic
                  glutenFree
                  guestMeals
                  halaal
                  kosher
                  lowOrNoSodium
                  noSugar
                  professionalChef
                  roomService
                  someMealsProvided
                  specialDietaryRestrictions
                  vegan
                  vegetarian
                }
                onSiteServices {
                  beautician
                  concierge
                  familyEducationAndSupport
                  housekeeping
                  laundryOrDryCleaning
                  moveInCoordination
                }
              }
              offerings {
                monthlyRent {
                  amount
                  currencyCode
                }
                communityType
              }
            }
          }
        }
      }
      ... on ProviderSearchError {
        __typename
        message
      }
    }
  }
`;

const UPDATE_SEEKER_CHILDREN_INFORMATION = gql`
  mutation updateSeekerChildrenInformation($block: [ChildInformationInput!]) {
    updateSeekerChildrenInformation(children: $block) {
      ... on UpdateSeekerChildrenInformationSuccess {
        success
        __typename
      }
      __typename
    }
  }
`;

const ENROLL_SEEKER_FOR_ENTERPRISE_CLIENT = gql`
  mutation enrollSeekerForEnterpriseClient(
    $employeeEnrollmentDetails: EnterpriseEmployeeEnrollmentDetails!
  ) {
    enrollSeekerForEnterpriseClient(employeeEnrollmentDetails: $employeeEnrollmentDetails) {
      ... on EnterpriseEmployeeEnrollmentSuccess {
        authToken
        memberId
        oneTimeToken
      }

      ... on EnterpriseEnrollmentProcessFailure {
        errors {
          errorMessages
        }
      }
    }
  }
`;

const CREATE_HOME_PAY_PROSPECT = gql`
  mutation createHomePayProspect($input: HomePayProspectInput!) {
    createHomePayProspect(input: $input) {
      __typename
      ... on CreateHomePayProspectSuccess {
        prospect {
          prospectId
        }
      }
    }
  }
`;

const GET_SEEKER_ZIP_CODE = gql`
  query getSeekerZipCode($memberId: ID!) {
    getSeeker(id: $memberId) {
      member {
        address {
          zip
        }
      }
    }
  }
`;

const GET_SEEKER_INFO = gql`
  query getSeekerInfo($memberId: ID!) {
    getSeeker(id: $memberId) {
      member {
        legacyId
        email
        firstName
        lastName
        contact {
          primaryPhone
        }
      }
    }
  }
`;

const GET_SITTER_INFO = gql`
  query getCaregiver($id: ID!, $serviceId: ServiceType) {
    getCaregiver(id: $id, serviceId: $serviceId) {
      member {
        displayName
        firstName
        imageURL
        id
        address {
          zip
        }
      }
    }
  }
`;

const UPDATE_SEEKER_ATTRIBUTE = gql`
  mutation SeekerUpdate($input: SeekerUpdateInput!) {
    seekerUpdate(input: $input) {
      success
    }
  }
`;

const SAVE_PROVIDER_MULTIPLE_VERTICALS = gql`
  mutation SaveMultipleVerticals($input: MultipleVerticalsInput) {
    saveMultipleVerticals(input: $input) {
      ... on MultipleVerticalsUpdateSuccess {
        success
      }
      ... on MultipleVerticalsUpdateError {
        message
      }
    }
  }
`;

const TUTORING_PROVIDER_ATTRIBUTES_UPDATE = gql`
  mutation tutoringProviderAttributesUpdate($input: TutoringProviderAttributesUpdateInput!) {
    tutoringProviderAttributesUpdate(input: $input) {
      ... on TutoringProviderAttributesUpdateSuccess {
        success
      }
    }
  }
`;
const HOUSE_KEEPING_PROVIDER_ATTRIBUTES_UPDATE = gql`
  mutation houseKeepingProviderAttributesUpdate(
    $input: HouseKeepingProviderAttributesUpdateInput!
  ) {
    houseKeepingProviderAttributesUpdate(input: $input) {
      ... on HouseKeepingProviderAttributesUpdateSuccess {
        success
      }
    }
  }
`;
const PET_CARE_PROVIDER_ATTRIBUTES_UPDATE = gql`
  mutation petCareProviderAttributesUpdate($input: PetCareProviderAttributesUpdateInput!) {
    petCareProviderAttributesUpdate(input: $input) {
      ... on PetCareProviderAttributesUpdateSuccess {
        success
      }
    }
  }
`;
const LOG_CLICK_EVENT = gql`
  mutation LogClickEvent($input: LogClickEventInput!) {
    logClickEvent(input: $input) {
      ... on LogClickEventPayload {
        success
        status
      }
      ... on LogClickEventError {
        message
      }
    }
  }
`;

const GET_SOCIAL_PROFILE = gql`
  query SocialLoginProfile {
    getSocialLoginProfile {
      ... on SocialLoginProfile {
        appleId
        email
        familyName
        givenName
        googleId
        __typename
      }
      ... on SocialLoginProfileError {
        message
        __typename
      }
    }
  }
`;

const SAVE_CZEN_TEST_EXPOSURE = gql`
  mutation SaveCzenTestExposure($input: TestCellReferenceIdsInput!) {
    saveCzenTestExposure(input: $input) {
      ... on SaveCzenTestExposureSuccess {
        success
      }
      ... on SaveCzenTestExposureError {
        error
      }
    }
  }
`;

const CAREGIVER_FRAGMENT = gql`
  fragment CaregiverFragment on SearchProvidersSuccess {
    __typename
    sourceType
    searchProvidersConnection {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          ... on Caregiver {
            member {
              id
              legacyId
              imageURL
              displayName
              firstName
              address {
                city
                state
                zip
              }
            }
            badges
            yearsOfExperience
            hiredByCounts {
              locality {
                hiredCount
              }
            }
            hiredTimes
            revieweeMetrics {
              ... on ReviewFailureResponse {
                message
              }
              ... on RevieweeMetricsPayload {
                metrics {
                  totalReviews
                  averageRatings {
                    type
                    value
                  }
                }
              }
            }
            profiles {
              commonCaregiverProfile {
                id
                bio {
                  experienceSummary
                }
              }
              childCareCaregiverProfile {
                bio {
                  experienceSummary
                  aiAssistedBio
                }
                rates {
                  hourlyRate {
                    amount
                  }
                }
                recurringRate {
                  hourlyRateFrom {
                    amount
                  }
                  hourlyRateTo {
                    amount
                  }
                }
              }
              petCareCaregiverProfile {
                bio {
                  experienceSummary
                  aiAssistedBio
                }
                recurringRate {
                  hourlyRateFrom {
                    amount
                  }
                  hourlyRateTo {
                    amount
                  }
                }
              }
              seniorCareCaregiverProfile {
                bio {
                  experienceSummary
                  aiAssistedBio
                }
                recurringRate {
                  hourlyRateFrom {
                    amount
                  }
                  hourlyRateTo {
                    amount
                  }
                }
              }
              houseKeepingCaregiverProfile {
                bio {
                  experienceSummary
                  aiAssistedBio
                }
                recurringRate {
                  hourlyRateFrom {
                    amount
                  }
                  hourlyRateTo {
                    amount
                  }
                }
              }
            }
            isFavorite
            responseTime
          }
          ... on SearchProvidersNodeError {
            providerId
            message
          }
        }
      }
    }
  }
`;

const CAREGIVER_NO_HIRED_BY_FRAGMENT = gql`
  fragment CaregiverNoHiredByFragment on SearchProvidersSuccess {
    __typename
    sourceType
    searchProvidersConnection {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          ... on Caregiver {
            member {
              id
              legacyId
              imageURL
              displayName
              firstName
              address {
                city
                state
                zip
              }
            }
            badges
            yearsOfExperience
            hiredTimes
            revieweeMetrics {
              ... on ReviewFailureResponse {
                message
              }
              ... on RevieweeMetricsPayload {
                metrics {
                  totalReviews
                  averageRatings {
                    type
                    value
                  }
                }
              }
            }
            profiles {
              commonCaregiverProfile {
                id
                bio {
                  experienceSummary
                }
              }
              childCareCaregiverProfile {
                bio {
                  experienceSummary
                  aiAssistedBio
                }
                rates {
                  hourlyRate {
                    amount
                  }
                }
                recurringRate {
                  hourlyRateFrom {
                    amount
                  }
                  hourlyRateTo {
                    amount
                  }
                }
              }
              seniorCareCaregiverProfile {
                bio {
                  experienceSummary
                  aiAssistedBio
                }
                recurringRate {
                  hourlyRateFrom {
                    amount
                  }
                  hourlyRateTo {
                    amount
                  }
                }
              }
            }
            isFavorite
            responseTime
          }
          ... on SearchProvidersNodeError {
            providerId
            message
          }
        }
      }
    }
  }
`;

const SEARCH_PROVIDERS_CHILDCARE = gql`
  ${CAREGIVER_FRAGMENT}
  query SearchProvidersChildCare($input: SearchProvidersChildCareInput!) {
    searchProvidersChildCare(input: $input) {
      ... on SearchProvidersSuccess {
        ...CaregiverFragment
      }
      ... on SearchProvidersError {
        message
      }
    }
  }
`;

export const SEARCH_PROVIDERS_CHILDCARE_NO_HIRED_BY = gql`
  ${CAREGIVER_NO_HIRED_BY_FRAGMENT}
  query SearchProvidersChildCareNoHiredBy($input: SearchProvidersChildCareInput!) {
    searchProvidersChildCare(input: $input) {
      ... on SearchProvidersSuccess {
        ...CaregiverNoHiredByFragment
      }
      ... on SearchProvidersError {
        message
      }
    }
  }
`;

const REVIEWS_BY_REVIEWEE = gql`
  query ReviewsByReviewee(
    $revieweeId: ID!
    $revieweeType: ReviewInfoEntityType!
    $careType: ReviewInfoCareType
    $pageSize: Int
    $pageToken: String
  ) {
    reviewsByReviewee(
      revieweeId: $revieweeId
      revieweeType: $revieweeType
      careType: $careType
      pageSize: $pageSize
      pageToken: $pageToken
    ) {
      ... on ReviewsByRevieweePayload {
        __typename
        nextPageToken
        reviews {
          createTime
          description {
            originalText
          }
          id
          ratings {
            value
          }
          reviewer {
            imageURL
            publicMemberInfo {
              firstName
              lastInitial
            }
          }
        }
      }
      ... on ReviewFailureResponse {
        message
      }
    }
  }
`;

export const SEARCH_PROVIDERS_SENIORCARE = gql`
  ${CAREGIVER_FRAGMENT}
  query SearchProvidersSeniorCare($input: SearchProvidersSeniorCareInput!) {
    searchProvidersSeniorCare(input: $input) {
      ... on SearchProvidersSuccess {
        ...CaregiverFragment
      }
      ... on SearchProvidersError {
        message
      }
    }
  }
`;

export const SEARCH_PROVIDERS_SENIORCARE_NO_HIRED_BY = gql`
  ${CAREGIVER_NO_HIRED_BY_FRAGMENT}
  query SearchProvidersSeniorCareNoHiredBy($input: SearchProvidersSeniorCareInput!) {
    searchProvidersSeniorCare(input: $input) {
      ... on SearchProvidersSuccess {
        ...CaregiverNoHiredByFragment
      }
      ... on SearchProvidersError {
        message
      }
    }
  }
`;

export const SEARCH_PROVIDERS_HOUSEKEEPING = gql`
  ${CAREGIVER_FRAGMENT}
  query SearchProvidersHousekeeping($input: SearchProvidersHousekeepingInput!) {
    searchProvidersHousekeeping(input: $input) {
      ... on SearchProvidersSuccess {
        ...CaregiverFragment
      }
      ... on SearchProvidersError {
        message
      }
    }
  }
`;

export const SEARCH_PROVIDERS_HOUSEKEEPING_NO_HIRED_BY = gql`
  ${CAREGIVER_NO_HIRED_BY_FRAGMENT}
  query SearchProvidersHousekeepingNoHiredBy($input: SearchProvidersHousekeepingInput!) {
    searchProvidersHousekeeping(input: $input) {
      ... on SearchProvidersSuccess {
        ...CaregiverNoHiredByFragment
      }
      ... on SearchProvidersError {
        message
      }
    }
  }
`;
export const SEARCH_PROVIDERS_PET_CARE_NO_HIRED_BY = gql`
  ${CAREGIVER_NO_HIRED_BY_FRAGMENT}
  query searchProvidersPetCareNoHiredBy($input: SearchProvidersPetCareInput!) {
    searchProvidersPetCare(input: $input) {
      ... on SearchProvidersSuccess {
        ...CaregiverNoHiredByFragment
      }
      ... on SearchProvidersError {
        message
      }
    }
  }
`;

export const SEARCH_PROVIDERS_PET_CARE = gql`
  ${CAREGIVER_FRAGMENT}
  query searchProvidersPetCare($input: SearchProvidersPetCareInput!) {
    searchProvidersPetCare(input: $input) {
      ... on SearchProvidersSuccess {
        ...CaregiverFragment
      }
      ... on SearchProvidersError {
        message
      }
    }
  }
`;

export {
  PROVIDER_JOB_INTEREST_UPDATE,
  CAREGIVER_ATTRIBUTES_UPDATE,
  PROVIDER_NAME_UPDATE,
  GET_DEFAULT_JOB_WAGES,
  PROVIDER_AVAILABILITY_UPDATE,
  FAVORITE_PROVIDER,
  GET_JOB_WAGES,
  GET_AVERAGE_JOB_WAGE,
  PROVIDER_CREATE,
  GET_NUMBER_OF_JOBS_NEARBY,
  GET_NUMBER_OF_SENIOR_CARE_FACILITIES_NEARBY,
  SENIOR_CARE_PROVIDER_ATTRIBUTES_UPDATE,
  GET_CAREGIVER_ENROLLMENT_STATUS,
  SENIOR_CARE_SEEKER_CREATE,
  GET_ZIP_CODE_SUMMARY,
  GET_CAREGIVER_COUNT_FOR_JOB,
  VALIDATE_MEMBER_EMAIL,
  VALIDATE_MEMBER_PASSWORD,
  SEEKER_CREATE,
  CHILD_CARE_PROVIDER_EXPANDED,
  CHANGE_MEMBER_PASSWORD,
  POST_CHILD_CARE_LEAD,
  POST_AUTO_ACCEPT_CHILD_CARE_LEAD,
  GET_TAX_CALCULATOR_PRICING,
  SENIOR_CARE_FACILITY_LEAD_GENERATE,
  HIRED_CAREGIVER_CRM_EVENT_CREATE,
  DAYCARE_CRM_EVENT_CREATE,
  SENIOR_CARE_ASSISTED_LIVING_PROVIDERS,
  ENROLL_SEEKER_FOR_ENTERPRISE_CLIENT,
  CREATE_HOME_PAY_PROSPECT,
  GET_SEEKER_ZIP_CODE,
  GET_SEEKER_INFO,
  GET_SITTER_INFO,
  UPDATE_SEEKER_ATTRIBUTE,
  GET_INSTANT_BOOK_LOCATION_ELIGIBLE,
  SAVE_PROVIDER_MULTIPLE_VERTICALS,
  HOUSE_KEEPING_PROVIDER_ATTRIBUTES_UPDATE,
  TUTORING_PROVIDER_ATTRIBUTES_UPDATE,
  PET_CARE_PROVIDER_ATTRIBUTES_UPDATE,
  LOG_CLICK_EVENT,
  GET_SOCIAL_PROFILE,
  SAVE_CZEN_TEST_EXPOSURE,
  UPDATE_SEEKER_CHILDREN_INFORMATION,
  SEARCH_PROVIDERS_CHILDCARE,
  REVIEWS_BY_REVIEWEE,
};
